import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";


import CloseIcon from "@mui/icons-material/Close";

import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";

import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import apiService from ".././../../../../service/Api";
import PrintableInvoice from "./PrintableInvoice";
import  "./Invoice.css";

interface Props {
  open: boolean;
  onClose: () => void;
  streamId: string;
}

interface Service {
  id: number;
  name: string;
  description: string;
  price: string;
}

interface Company {
  name: string;
  company_logo: string |null;
  phone_number: string;
  website: string;
  street_address: string;
  city: string;
  state: string;
}

interface Client {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface Deceased {
  id: number;
  name: string;
}

export interface Package {
  id: number;
  name: string;
  price: string;
  currency: string;
  description: string;
  extra_services: Service[];
  package_name: string;
  package_type: string;
  package_description: string;
}


interface InvoiceDetails {
  id:number;
  invoice_number:string;
  title:string;
  total_price:string;
  created_at:string;
}
interface GenerateInvoiceResponse {
  message: string;
  created: boolean;
  invoice: InvoiceDetails;
}

interface InvoiceResponse {
  company:Company;
  client:Client;
  deceased:Deceased[];
  package:Package;
  invoice:InvoiceDetails;
}

const Invoice:React.FC<Props> = ({ streamId , open,onClose, }) => {

const [invoice,setInvoice] = useState<InvoiceResponse | null>(null);

const [loading,setLoading] = useState(true);


const invoiceRef = useRef<HTMLDivElement>(null);

const fetchInvoice = useCallback(async()=>{

try{

      setLoading(true);

      const response =
      await apiService.get<InvoiceResponse>(
      `api/streams/${streamId}/invoice-details/`
      );

      setInvoice(response.data);


}
catch(error){

      console.error(error);

      setInvoice(null);

      }
finally{setLoading(false);}

},[streamId]);

useEffect(() => {
  if (open && streamId) {
    fetchInvoice();
  }
}, [open, streamId, fetchInvoice]);


const handleGenerateInvoice = async () => {
    try {
        const response = await apiService.post<GenerateInvoiceResponse>(
        `api/streams/${streamId}/generate-invoice/`
        );
        

        alert(response.data.message);

        // Refresh the invoice after generating it
        fetchInvoice();

    } catch (error) {
        console.error(error);
        alert("Failed to generate invoice.");
    }
};

const handlePrint = useReactToPrint({

contentRef:invoiceRef,

documentTitle:
invoice?.invoice?.invoice_number || "Invoice"

});

const handleDownload = async()=>{

if(!invoiceRef.current) return;

const canvas = await html2canvas(invoiceRef.current, {
    scale: 3,
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false,
});

const imgData = canvas.toDataURL("image/png");

const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
});

const pageWidth =
pdf.internal.pageSize.getWidth();

const pageHeight =
pdf.internal.pageSize.getHeight();

const margin = 8;

const imgWidth = pageWidth - margin * 2;

const imgHeight =
(canvas.height*imgWidth)/canvas.width;

let heightLeft = imgHeight;

let position = 0;

pdf.addImage(
    imgData,
    "PNG",
    margin,
    position,
    imgWidth,
    imgHeight
);
heightLeft -= pageHeight;

while(heightLeft>0){

position = heightLeft-imgHeight;

pdf.addPage();

pdf.addImage(
    imgData,
    "PNG",
    margin,
    position,
    imgWidth,
    imgHeight
);

heightLeft -= pageHeight;

}

pdf.save(
`${invoice?.invoice?.invoice_number ?? "Invoice"}.pdf`
);

};

if (!open) {
  return null;
}

if(loading){

return(

<Card>

<CardContent>

<Box
display="flex"
justifyContent="center"
py={5}
>

<CircularProgress/>

</Box>

</CardContent>

</Card>

);

}

if (
  !invoice ||
  !invoice.invoice ||
  !invoice.package
) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Invoice</DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          No invoice has been generated for this stream.
        </Alert>

        <Button
          variant="contained"
          onClick={handleGenerateInvoice}
        >
          Generate Invoice
        </Button>
      </DialogContent>
    </Dialog>
  );
}
return (
    <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        
    >
        <DialogTitle>
            Invoice

            <IconButton
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                }}
            >
                <CloseIcon />
            </IconButton>
        </DialogTitle>

        <DialogContent dividers>

             <div ref={invoiceRef}>
            <PrintableInvoice
                invoice={invoice}
            />
        </div>

        

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          className="no-print"
          sx={{
            mt: 4,
            flexWrap: "wrap",
          }}
        >


          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>

          <Tooltip title="Coming up soon">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled
                  /* onClick={handleDownload} */
                >
                  Download PDF
                </Button>
              </span>
            </Tooltip>

        </Stack>


        </DialogContent>

    </Dialog>
);
}
export default Invoice;